import React from 'react';
import { useSelector } from 'react-redux';

import CustomScrollbars from 'components/molecules/CustomScrollBars';
import Navigation from 'components/molecules/Navigation';
import { permissionList, usePermission } from 'hooks/usePermission';

const SideBarContent = () => {
  const { authUser } = useSelector((state) => state.auth);
  const { evaluate } = usePermission();
  const isMerchant = authUser.type === 'merchant';

  const hasMenuDashboard = evaluate([permissionList.menuDashboard]);

  const adminHasWidgets =
    evaluate([permissionList.amountTransactionRead]) ||
    evaluate([permissionList.profitRead]) ||
    evaluate([permissionList.adjustmentFeeReportRead]) ||
    evaluate([permissionList.bankfeeRead]) ||
    evaluate([permissionList.summaryPendingWithdrawal]);

  const merchantHasWidgets = evaluate([permissionList.transactionRead]) || evaluate([permissionList.walletRead]);

  const showDashboard = hasMenuDashboard && (isMerchant ? merchantHasWidgets : adminHasWidgets);

  const rendersOptions = (array) => {
    return array.map((item) => {
      const hasPermission = evaluate(item?.required);

      item.type = hasPermission || item?.conditional ? 'item' : 'hide';
      return item;
    });
  };
  const userMenu = [
    {
      name: 'app.menu.customer',
      icon: 'menu',
      link: '/app/users/customer',
      required: [permissionList.customerRead],
    },
    {
      name: 'app.menu.merchant',
      icon: 'menu',
      link: isMerchant ? '/app/users/own-merchant' : '/app/users/merchant',
      required: isMerchant ? [permissionList.userMerchantReadOwn] : [permissionList.userMerchantRead],
    },
    {
      name: 'app.menu.administrator',
      icon: 'menu',
      link: '/app/users/administrator',
      required: [permissionList.userRead],
    },
    {
      name: 'app.menu.banksByUser',
      icon: 'menu',
      link: '/app/users/banks',
      required: [permissionList.customerRead, permissionList.customerBankRead],
    },
  ];
  const transactionsMenu = [
    {
      name: 'app.menu.transactions.internal',
      icon: 'view-list-alt',
      required: [permissionList.transactionRead],
      link: '/app/transactions',
    },
    {
      name: 'app.menu.externalTransactions',
      icon: 'view-list-alt',
      required: [permissionList.transactionExternalRead],
      link: '/app/external-transactions',
    },
    {
      name: 'app.menu.transactions.errors',
      icon: 'alert-circle-o',
      required: [permissionList.transactionErrorsRead],
      link: '/app/transactions-errors',
    },
    {
      name: 'app.menu.approvals',
      icon: 'check-all',
      required: [permissionList.customerRead, permissionList.transactionReviewRead],
      link: '/app/approvals/transactions',
    },
    // {
    //   name: 'app.menu.issues',
    //   icon: 'alert-polygon',
    //   required: [permissionList.transactionIssueRead],
    //   link: '/app/issues',
    // },
    {
      name: 'app.menu.transactions.adjustment',
      icon: 'unfold-more',
      link: '/app/transactions-adjustment',
      required: [permissionList.transactionsAdjustment],
    },
    {
      name: 'app.menu.transactions.simulate',
      icon: 'repeat-one',
      link: '/app/transactions-simulate',
      required: [permissionList.transactionsSimulate],
    },
  ];
  const reportsMenu = [
    {
      name: 'app.menu.accountStatement',
      icon: 'chart',
      link: '/app/statement',
      conditional:
        evaluate([permissionList.merchantDailyReportsRead]) || evaluate([permissionList.merchantMonthlyReportsRead]),
    },
    {
      name: 'app.menu.balancesHistory',
      icon: 'timer',
      link: '/app/wallet-balance-history',
      required: [permissionList.menuBalanceHistory],
    },
    {
      name: 'app.menu.balances',
      icon: 'chart',
      link: '/app/balances',
      conditional:
        evaluate([permissionList.menuBalances, permissionList.merchantRead]) || evaluate([permissionList.bankRead]),
    },
    {
      name: 'app.menu.dailyReport',
      icon: 'chart',
      link: '/app/daily-report',
      required: [permissionList.summaryDailyTransactionsRead],
    },
    {
      name: 'app.menu.customerTransactions',
      icon: 'chart',
      link: '/app/customer-transactions',
      required: [permissionList.summaryTransactionsCustomerRead],
    },
    {
      name: 'app.menu.notifications',
      icon: 'chart',
      link: '/app/notifications-report',
      required: [permissionList.notificationRead],
    },
    {
      name: 'app.menu.providerRequestsReport',
      icon: 'chart',
      link: '/app/provider-requests-report',
      required: [permissionList.providerRequestRead],
    },
  ];

  const banksMenu = [
    {
      name: 'app.menu.banks.list',
      icon: 'menu',
      link: '/app/banks/management',
      required: [permissionList.menuBanks, permissionList.bankRead],
    },
    {
      name: 'app.menu.banks.global',
      icon: 'globe',
      link: '/app/banks/global',
      required: [permissionList.menuBanks, permissionList.bankRead, permissionList.countTransactionsBanksRead],
    },
    {
      name: 'app.menu.banks.weightingByMerchant',
      icon: 'layers',
      link: '/app/banks/weighting',
      required: [permissionList.menuBanks],
    },
    {
      name: 'app.menu.monitor',
      icon: 'desktop-mac',
      link: '/app/banks/monitor',
      required: [permissionList.conversionMonitorRead],
    },
  ];

  const pixAndGoMenu = [
    {
      name: 'app.menu.pixAndGo.qrs',
      icon: 'menu',
      link: '/app/pix-and-go/qrs',
      required: [permissionList.pixAndGoRead],
    },
    {
      name: 'app.menu.pixAndGo.transactions',
      icon: 'menu',
      link: '/app/pix-and-go/transactions',
      required: [permissionList.pixAndGoRead],
    },
  ];

  const navigationMenus = [
    {
      name: 'app.menu.dashboard',
      icon: 'view-dashboard',
      type: showDashboard ? 'item' : 'hide',
      link: '/app/dashboard',
    },
    {
      name: 'app.menu.users',
      icon: 'account-box',
      type: rendersOptions(userMenu).some((item) => item.type === 'item') ? 'collapse' : 'hide',
      children: rendersOptions(userMenu),
      link: '/app/users',
      parentLinks: ['/app/users', '/app/create/user'],
    },
    {
      name: 'app.menu.transactions',
      icon: 'swap',
      type: rendersOptions(transactionsMenu).some((item) => item.type === 'item') ? 'collapse' : 'hide',
      children: rendersOptions(transactionsMenu),
      link: '/app/transactions',
    },
    {
      name: 'app.menu.merchants',
      icon: 'local-store',
      type: evaluate([permissionList.menuMerchants, permissionList.merchantRead]) && !isMerchant ? 'item' : 'hide',
      link: '/app/merchants',
    },
    {
      name: 'app.menu.banks',
      icon: 'balance',
      type: rendersOptions(banksMenu).some((item) => item.type === 'item') && !isMerchant ? 'collapse' : 'hide',
      children: rendersOptions(banksMenu),
      link: '/app/banks/global',
      parentLinks: ['/app/banks'],
    },
    {
      name: 'app.menu.cashout',
      icon: 'money-box',
      type: evaluate([permissionList.bankCashoutRead]) && !isMerchant ? 'item' : 'hide',
      link: '/app/cashout',
    },
    {
      name: 'app.menu.reports',
      icon: 'chart',
      type: rendersOptions(reportsMenu).some((item) => item.type === 'item') ? 'collapse' : 'hide',
      link: '/app/reports',
      children: rendersOptions(reportsMenu),
    },
    {
      name: 'app.menu.audits',
      icon: 'shield-check',
      type: evaluate([permissionList.auditRead]) ? 'item' : 'hide',
      link: '/app/audits',
    },
    {
      name: 'app.menu.sof',
      icon: 'receipt',
      type: evaluate([permissionList.customerDocumentRead]) ? 'item' : 'hide',
      link: '/app/approvals/sof',
    },
    {
      name: 'app.menu.credentials',
      icon: 'menu',
      type: evaluate([permissionList.oAuthClientRead]) && isMerchant ? 'item' : 'hide',
      link: '/app/merchant-credentials',
    },
    {
      name: 'app.menu.pixAndGo',
      icon: 'money-box',
      type: rendersOptions(pixAndGoMenu).some((item) => item.type === 'item') && !isMerchant ? 'collapse' : 'hide',
      children: rendersOptions(pixAndGoMenu),
      link: '/app/pix-and-go/qrs',
      parentLinks: ['/app/pix-and-go'],
    },
  ];

  return (
    <CustomScrollbars className=" scrollbar">
      <Navigation menuItems={navigationMenus} permissions={authUser?.permissions} />
    </CustomScrollbars>
  );
};

export default SideBarContent;
